/** @jsxImportSource react */
import React from 'react';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import './PlatformStatusCard.css';

export type PlatformStatusVariant = 'maintenance' | 'hiring-pause';

export interface PlatformStatusCardProps {
  variant: PlatformStatusVariant;
  message: string;
  title?: string;
  hint?: string;
  primaryAction?: { label: string; onClick: () => void; loading?: boolean };
  secondaryAction?: { label: string; onClick: () => void };
  tertiaryAction?: { label: string; onClick: () => void };
}

const maintenanceIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true },
  React.createElement('path', {
    d: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
);

const pauseIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true },
  React.createElement('rect', { x: '6', y: '4', width: '4', height: '16', rx: '1', fill: 'currentColor' }),
  React.createElement('rect', { x: '14', y: '4', width: '4', height: '16', rx: '1', fill: 'currentColor' }),
);

export function PlatformStatusCard({
  variant,
  message,
  title,
  hint,
  primaryAction,
  secondaryAction,
  tertiaryAction,
}: PlatformStatusCardProps) {
  const { t } = useThemeLanguage();

  const defaultTitle =
    variant === 'maintenance' ? t('maintenance.title') : t('hiringPause.emptyStateTitle');

  const icon = variant === 'maintenance' ? maintenanceIconSvg : pauseIconSvg;

  return React.createElement(
    'div',
    {
      className: `platform-status-card platform-status-card--${variant}`,
      role: 'alert',
      'aria-live': 'polite',
    },
    React.createElement(
      'div',
      { className: `platform-status-card__wrapper platform-status-card__wrapper--${variant}` },
      React.createElement(
        'div',
        { className: 'platform-status-card__inner' },
        React.createElement('span', { className: 'platform-status-card__icon' }, icon),
        React.createElement('h2', { className: 'platform-status-card__title' }, title ?? defaultTitle),
        React.createElement('p', { className: 'platform-status-card__message' }, message),
        hint && React.createElement('p', { className: 'platform-status-card__hint' }, hint),
        (primaryAction || secondaryAction || tertiaryAction) &&
          React.createElement(
            'div',
            { className: 'platform-status-card__actions' },
            tertiaryAction &&
              React.createElement(
                'button',
                {
                  type: 'button',
                  className: 'platform-status-card__btn platform-status-card__btn--tertiary',
                  onClick: tertiaryAction.onClick,
                },
                tertiaryAction.label,
              ),
            primaryAction &&
              React.createElement(
                'button',
                {
                  type: 'button',
                  className: 'platform-status-card__btn platform-status-card__btn--primary',
                  onClick: primaryAction.onClick,
                  disabled: primaryAction.loading,
                },
                primaryAction.loading ? t('maintenance.checking') : primaryAction.label,
              ),
            secondaryAction &&
              React.createElement(
                'button',
                {
                  type: 'button',
                  className: 'platform-status-card__btn platform-status-card__btn--secondary',
                  onClick: secondaryAction.onClick,
                },
                secondaryAction.label,
              ),
          ),
      ),
    ),
  );
}
